import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Trash2, DollarSign, Eye, Edit, XCircle, CheckCircle, FileText, Search, Package, Wrench } from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import { ServiceOrder, ServiceOrderStatus, PaymentMethod, Product } from '@/types';
import {
  useServiceOrders,
  usePendingServiceOrders,
  useServiceOrder,
  useCreateServiceOrder,
  useAddServiceOrderItem,
  useUpdateServiceOrderItem,
  useRemoveServiceOrderItem,
  useMarkServiceOrderReady,
  useChargeServiceOrder,
  useCancelServiceOrder,
  useDeleteServiceOrder,
} from '@/hooks/useServiceOrders';
import { usePatients } from '@/hooks/usePatients';
import { useProducts, useCategories } from '@/hooks/useProducts';
import { ORDER_STATUS_LABELS, ORDER_STATUS_COLORS } from '@/services/serviceOrders';
import { useAuthStore } from '@/context/authStore';
import { formatPrice, formatDateTime } from '@/utils/format';

type ViewTab = 'all' | 'pending';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'open', label: 'Abierta' },
  { value: 'ready_to_charge', label: 'Lista para Cobrar' },
  { value: 'charged', label: 'Cobrada' },
  { value: 'cancelled', label: 'Cancelada' },
];

export default function ServiceOrders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ViewTab>('pending');
  const [statusFilter, setStatusFilter] = useState<ServiceOrderStatus | ''>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isChargeModalOpen, setIsChargeModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ServiceOrder | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ServiceOrder | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState<ServiceOrder | null>(null);

  // Obtener comanda desde URL params (para editar desde peluquería)
  const editOrderId = searchParams.get('edit');
  const { data: orderToEdit } = useServiceOrder(editOrderId ? parseInt(editOrderId) : null);

  // Abrir modal de edición cuando llega desde URL
  useEffect(() => {
    if (orderToEdit && editOrderId) {
      setSelectedOrder(orderToEdit);
      setIsDetailModalOpen(true);
      // Limpiar el parámetro de la URL para que no se reabra al cerrar
      setSearchParams({}, { replace: true });
    }
  }, [orderToEdit, editOrderId, setSearchParams]);

  // Datos
  const { data: allOrders = [], isLoading: loadingAll } = useServiceOrders(
    statusFilter ? { status: statusFilter as ServiceOrderStatus } : undefined
  );
  const { data: pendingOrders = [], isLoading: loadingPending } = usePendingServiceOrders();

  const orders = activeTab === 'pending' ? pendingOrders : allOrders;
  const isLoading = activeTab === 'pending' ? loadingPending : loadingAll;

  // Mutations
  const createOrder = useCreateServiceOrder();
  const addItem = useAddServiceOrderItem();
  const updateItem = useUpdateServiceOrderItem();
  const removeItem = useRemoveServiceOrderItem();
  const markReady = useMarkServiceOrderReady();
  const chargeOrder = useChargeServiceOrder();
  const cancelOrder = useCancelServiceOrder();
  const deleteOrder = useDeleteServiceOrder();

  const handleViewDetail = (order: ServiceOrder) => {
    setSelectedOrder(order);
    setIsDetailModalOpen(true);
  };

  const handleCharge = (order: ServiceOrder) => {
    setSelectedOrder(order);
    setIsChargeModalOpen(true);
  };

  const handleDelete = async () => {
    if (deleteConfirm) {
      try {
        await deleteOrder.mutateAsync(deleteConfirm.id);
        setDeleteConfirm(null);
      } catch (error) {
        console.error('Error deleting:', error);
      }
    }
  };

  const handleCancel = async () => {
    if (cancelConfirm) {
      try {
        await cancelOrder.mutateAsync(cancelConfirm.id);
        setCancelConfirm(null);
      } catch (error) {
        console.error('Error cancelling:', error);
      }
    }
  };

  const { user } = useAuthStore();
  const canManageOrders = user?.role === 'admin' || user?.role === 'superadmin' || (user?.effective_permissions?.includes('service_orders') ?? false);
  const isAdmin = canManageOrders;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Comandas</h1>
          <p className="text-gray-600">Gestión de comandas de servicio</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nueva Comanda
        </Button>
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="flex items-center gap-4">
          {/* Tabs como botones */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'pending'
                  ? 'bg-primary-100 text-primary-700 border border-primary-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Pendientes
              {pendingOrders.length > 0 && (
                <span className="ml-2 bg-yellow-500 text-white px-2 py-0.5 rounded-full text-xs">
                  {pendingOrders.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'all'
                  ? 'bg-primary-100 text-primary-700 border border-primary-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Todas
            </button>
          </div>

          {/* Filtro de estado (solo para "Todas") */}
          {activeTab === 'all' && (
            <div className="w-48">
              <Select
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as ServiceOrderStatus | '')}
              />
            </div>
          )}
        </div>
      </div>

      {/* Tabla de comandas */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="text-center py-8">Cargando...</div>
        ) : orders && orders.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Comanda
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Paciente / Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Items
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-taupe-100 rounded-lg flex items-center justify-center">
                          <FileText className="h-5 w-5 text-taupe-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            #{order.id}
                          </div>
                          <div className="text-sm text-gray-500">
                            {order.created_by_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {order.patient_name || '-'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {order.owner_name || 'Sin asignar'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${ORDER_STATUS_COLORS[order.status]}`}>
                        {ORDER_STATUS_LABELS[order.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {order.items.length} items
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-bold text-green-600">
                        {formatPrice(order.total)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(order.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex justify-end gap-1">
                        {/* Ver/Editar */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetail(order)}
                          title="Ver / Editar"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>

                        {/* Lista para Cobrar (solo si está abierta) */}
                        {order.status === 'open' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => markReady.mutate(order.id)}
                            title="Marcar lista para cobrar"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}

                        {/* Cobrar (solo si está lista) */}
                        {order.status === 'ready_to_charge' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCharge(order)}
                            title="Cobrar"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <DollarSign className="h-4 w-4" />
                          </Button>
                        )}

                        {/* Cancelar (abierta, o lista para cobrar si es admin) */}
                        {(order.status === 'open' || (order.status === 'ready_to_charge' && isAdmin)) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCancelConfirm(order)}
                            title="Cancelar"
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        )}

                        {/* Eliminar (abierta, cancelada, o lista para cobrar si es admin) */}
                        {(order.status === 'open' || order.status === 'cancelled' || (order.status === 'ready_to_charge' && isAdmin)) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(order)}
                            title="Eliminar"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {activeTab === 'pending'
              ? 'No hay comandas pendientes de cobro'
              : 'No hay comandas para mostrar'}
          </div>
        )}
      </div>

      {/* Modal crear comanda */}
      <CreateOrderModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={createOrder.mutateAsync}
      />

      {/* Modal detalle */}
      <OrderDetailModal
        order={selectedOrder}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedOrder(null);
        }}
        canEdit={isAdmin}
        onAddItem={async (item) => {
          if (selectedOrder && isAdmin) {
            const updated = await addItem.mutateAsync({ orderId: selectedOrder.id, item });
            setSelectedOrder(updated);
          }
        }}
        onUpdateItem={async (itemId, data) => {
          if (selectedOrder && isAdmin) {
            const updated = await updateItem.mutateAsync({ orderId: selectedOrder.id, itemId, data });
            setSelectedOrder(updated);
          }
        }}
        onRemoveItem={async (itemId) => {
          if (selectedOrder && isAdmin) {
            const updated = await removeItem.mutateAsync({ orderId: selectedOrder.id, itemId });
            setSelectedOrder(updated);
          }
        }}
      />

      {/* Modal cobrar */}
      <ChargeOrderModal
        order={selectedOrder}
        isOpen={isChargeModalOpen}
        onClose={() => {
          setIsChargeModalOpen(false);
          setSelectedOrder(null);
        }}
        onCharge={chargeOrder.mutateAsync}
      />

      {/* Modal confirmar eliminar */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Eliminar Comanda"
        size="sm"
      >
        {deleteConfirm && (
          <div className="space-y-4">
            <p className="text-gray-600">
              ¿Estás seguro de eliminar la comanda <strong>#{deleteConfirm.id}</strong>?
            </p>
            <p className="text-sm text-red-600">
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                Cancelar
              </Button>
              <Button variant="danger" onClick={handleDelete} loading={deleteOrder.isPending}>
                Eliminar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal confirmar cancelar */}
      <Modal
        isOpen={!!cancelConfirm}
        onClose={() => setCancelConfirm(null)}
        title="Cancelar Comanda"
        size="sm"
      >
        {cancelConfirm && (
          <div className="space-y-4">
            <p className="text-gray-600">
              ¿Estás seguro de cancelar la comanda <strong>#{cancelConfirm.id}</strong>?
            </p>
            <p className="text-sm text-gray-500">
              Esta acción marcará la comanda como cancelada.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setCancelConfirm(null)}>
                Volver
              </Button>
              <Button variant="danger" onClick={handleCancel} loading={cancelOrder.isPending}>
                Cancelar Comanda
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// Componente para crear comanda
function CreateOrderModal({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: any) => Promise<ServiceOrder>;
}) {
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: patients = [] } = usePatients({ search: patientSearch || undefined });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onCreate({
        patient_id: selectedPatientId || undefined,
        notes: notes || undefined,
        items: [],
      });
      onClose();
      setSelectedPatientId(null);
      setNotes('');
      setPatientSearch('');
    } catch (error) {
      console.error('Error creating order:', error);
    }
    setIsSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Nueva Comanda" size="md">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Buscar mascota (opcional)
          </label>
          <Input
            type="text"
            placeholder="Buscar por nombre..."
            value={patientSearch}
            onChange={(e) => setPatientSearch(e.target.value)}
          />
          {patients.length > 0 && patientSearch && (
            <div className="mt-2 border rounded-md max-h-40 overflow-y-auto">
              {patients.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setSelectedPatientId(p.id);
                    setPatientSearch(`${p.name} - ${p.owner?.first_name} ${p.owner?.last_name}`);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100"
                >
                  <span className="font-medium">{p.name}</span>
                  <span className="text-gray-500 text-sm ml-2">
                    ({p.species}) - {p.owner?.first_name} {p.owner?.last_name}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Observaciones..."
          />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} loading={isSubmitting}>
            Crear Comanda
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Componente detalle de comanda (solo lectura, edición solo para admin)
function OrderDetailModal({
  order: initialOrder,
  isOpen,
  onClose,
  canEdit,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
}: {
  order: ServiceOrder | null;
  isOpen: boolean;
  onClose: () => void;
  canEdit: boolean;
  onAddItem: (item: { product_id?: number; custom_name?: string; quantity: number; unit_price?: number; ml_quantity?: number }) => Promise<void>;
  onUpdateItem: (itemId: number, data: { quantity?: number; unit_price?: number; notes?: string }) => Promise<void>;
  onRemoveItem: (itemId: number) => Promise<void>;
}) {
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isCustomItem, setIsCustomItem] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editingPrice, setEditingPrice] = useState('');

  // Usar React Query para mantener los datos sincronizados
  const { data: fetchedOrder } = useServiceOrder(isOpen && initialOrder?.id ? initialOrder.id : null);

  // Usar los datos frescos de React Query si están disponibles
  const order = fetchedOrder || initialOrder;

  // Solo permitir edición si es admin y la comanda está abierta o lista para cobrar
  const allowEdit = canEdit && (order?.status === 'open' || order?.status === 'ready_to_charge');

  const handleAddProduct = async (product: Product, mlQuantity?: number) => {
    if (!allowEdit) return;

    // Si es producto sell_by_ml, calcular el precio basado en ml
    if (product.sell_by_ml && mlQuantity && product.price_per_ml) {
      const calculatedPrice = mlQuantity * product.price_per_ml;
      await onAddItem({
        product_id: product.id,
        quantity: 1,
        unit_price: calculatedPrice,
        ml_quantity: mlQuantity,  // Para descuento de stock correcto
      });
    } else {
      await onAddItem({
        product_id: product.id,
        quantity: 1,
        unit_price: product.sale_price || undefined,
      });
    }
    setIsProductModalOpen(false);
  };

  const handleAddCustom = async () => {
    if (!allowEdit || !customName || !customPrice) return;
    await onAddItem({
      custom_name: customName,
      quantity,
      unit_price: parseFloat(customPrice),
    });
    setCustomName('');
    setCustomPrice('');
    setQuantity(1);
    setIsCustomItem(false);
  };

  if (!order) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={`Comanda #${order.id}`} size="lg">
        <div className="space-y-4">
          {/* Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Estado:</strong> {ORDER_STATUS_LABELS[order.status]}</p>
                <p><strong>Paciente:</strong> {order.patient_name || 'Sin asignar'}</p>
                <p><strong>Cliente:</strong> {order.owner_name || 'Sin asignar'}</p>
              </div>
              <div>
                <p><strong>Creada:</strong> {formatDateTime(order.created_at)}</p>
                <p><strong>Por:</strong> {order.created_by_name}</p>
                {order.charged_at && (
                  <p><strong>Cobrada:</strong> {formatDateTime(order.charged_at)}</p>
                )}
              </div>
            </div>
          </div>

          {/* Items */}
          <div>
            <h3 className="font-semibold mb-2">Items</h3>
            {order.items.length === 0 ? (
              <p className="text-gray-500 text-sm">Sin items</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-2">Producto</th>
                    <th className="text-center p-2">Cant.</th>
                    <th className="text-right p-2">Precio</th>
                    <th className="text-right p-2">Subtotal</th>
                    {allowEdit && <th className="p-2"></th>}
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item) => (
                    <tr key={item.id} className="border-b">
                      <td className="p-2">
                        {item.product_name}
                        {item.ml_quantity && (
                          <span className="text-xs text-blue-600 ml-1">
                            ({item.ml_quantity} ml)
                          </span>
                        )}
                      </td>
                      <td className="text-center p-2">
                        {allowEdit ? (
                          <div className="flex items-center justify-center gap-1">
                            <button
                              type="button"
                              onClick={() => {
                                if (item.quantity > 1) {
                                  onUpdateItem(item.id, { quantity: item.quantity - 1 });
                                }
                              }}
                              disabled={item.quantity <= 1}
                              className="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-center"
                            >
                              -
                            </button>
                            <span className="w-8 text-center font-medium">{item.quantity}</span>
                            <button
                              type="button"
                              onClick={() => onUpdateItem(item.id, { quantity: item.quantity + 1 })}
                              className="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 text-sm flex items-center justify-center"
                            >
                              +
                            </button>
                          </div>
                        ) : (
                          item.quantity
                        )}
                      </td>
                      <td className="text-right p-2">
                        {allowEdit ? (
                          editingItemId === item.id ? (
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-gray-500">$</span>
                              <input
                                type="number"
                                className="w-24 px-2 py-1 text-right border rounded text-sm"
                                value={editingPrice}
                                onChange={(e) => setEditingPrice(e.target.value)}
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    const newPrice = parseFloat(editingPrice);
                                    if (!isNaN(newPrice) && newPrice >= 0) {
                                      onUpdateItem(item.id, { unit_price: newPrice });
                                    }
                                    setEditingItemId(null);
                                  } else if (e.key === 'Escape') {
                                    setEditingItemId(null);
                                  }
                                }}
                                onBlur={() => {
                                  const newPrice = parseFloat(editingPrice);
                                  if (!isNaN(newPrice) && newPrice >= 0) {
                                    onUpdateItem(item.id, { unit_price: newPrice });
                                  }
                                  setEditingItemId(null);
                                }}
                              />
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setEditingItemId(item.id);
                                setEditingPrice(item.unit_price.toString());
                              }}
                              className={`hover:bg-gray-100 px-2 py-1 rounded cursor-pointer ${item.unit_price === 0 ? 'text-red-500 font-medium' : ''}`}
                              title="Click para editar precio"
                            >
                              {formatPrice(item.unit_price)}
                              {item.unit_price === 0 && <Edit className="h-3 w-3 inline ml-1" />}
                            </button>
                          )
                        ) : (
                          <span>{formatPrice(item.unit_price)}</span>
                        )}
                      </td>
                      <td className="text-right p-2 font-medium">{formatPrice(item.subtotal)}</td>
                      {allowEdit && (
                        <td className="p-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onRemoveItem(item.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50">
                    <td colSpan={allowEdit ? 3 : 3} className="text-right p-2 font-bold">
                      Total:
                    </td>
                    <td className="text-right p-2 font-bold text-lg text-green-600" colSpan={allowEdit ? 2 : 1}>
                      {formatPrice(order.total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* Agregar item (solo admin y comanda abierta) */}
          {allowEdit && (
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Agregar Item (Admin)</h3>

              {isCustomItem ? (
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Item personalizado</p>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      type="text"
                      placeholder="Nombre del item..."
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                    />
                    <Input
                      type="number"
                      min={1}
                      value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                      placeholder="Cantidad"
                    />
                    <Input
                      type="number"
                      value={customPrice}
                      onChange={(e) => setCustomPrice(e.target.value)}
                      placeholder="Precio $"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleAddCustom} disabled={!customName || !customPrice}>
                      <Plus className="h-4 w-4 mr-1" />
                      Agregar
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setIsCustomItem(false)}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button onClick={() => setIsProductModalOpen(true)}>
                    <Package className="h-4 w-4 mr-2" />
                    Buscar Producto
                  </Button>
                  <Button variant="secondary" onClick={() => setIsCustomItem(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Item Personalizado
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t">
            <Button variant="secondary" onClick={onClose}>
              Cerrar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de selección de productos */}
      <ProductSelectionModal
        isOpen={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        onSelect={handleAddProduct}
      />
    </>
  );
}

// Modal grande para seleccionar productos
function ProductSelectionModal({
  isOpen,
  onClose,
  onSelect,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (product: Product, mlQuantity?: number) => void;
}) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'product' | 'service'>('all');
  const [stockError, setStockError] = useState<string | null>(null);
  // Estado para productos sell_by_ml
  const [mlProduct, setMlProduct] = useState<Product | null>(null);
  const [mlQuantity, setMlQuantity] = useState<string>('');

  // Pasar el filtro de tipo al backend
  const { data: products = [], isLoading } = useProducts(
    {
      search: search || undefined,
      limit: 50,
      product_type: typeFilter !== 'all' ? typeFilter : undefined,
    },
    { enabled: isOpen }
  );

  const handleSelect = (product: Product) => {
    // Validar stock para productos (no servicios)
    if (!product.is_service) {
      const stock = product.current_stock ?? 0;
      if (stock <= 0) {
        setStockError(`No hay stock disponible de "${product.name}"`);
        setTimeout(() => setStockError(null), 3000);
        return;
      }
    }

    // Si es producto sell_by_ml, mostrar formulario para ingresar ML
    if (product.sell_by_ml && product.price_per_ml) {
      setMlProduct(product);
      setMlQuantity('');
      return;
    }

    onSelect(product);
    setSearch('');
  };

  const handleMlConfirm = () => {
    if (!mlProduct || !mlQuantity) return;
    const ml = parseFloat(mlQuantity);
    if (isNaN(ml) || ml <= 0) return;

    // Validar que hay suficiente stock (en ml)
    const stock = mlProduct.current_stock ?? 0;
    if (ml > stock) {
      setStockError(`Stock insuficiente. Disponible: ${stock} ml`);
      setTimeout(() => setStockError(null), 3000);
      return;
    }

    onSelect(mlProduct, ml);
    setMlProduct(null);
    setMlQuantity('');
    setSearch('');
  };

  const handleMlCancel = () => {
    setMlProduct(null);
    setMlQuantity('');
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Seleccionar Producto" size="xl">
      <div className="space-y-4">
        {/* Mensaje de error de stock */}
        {stockError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
            {stockError}
          </div>
        )}

        {/* Formulario para ingresar ML (productos sell_by_ml) */}
        {mlProduct && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{mlProduct.name}</p>
                <p className="text-sm text-gray-500">
                  Precio por ml: {formatPrice(mlProduct.price_per_ml ?? 0)} | Stock: {mlProduct.current_stock} ml
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cantidad (ml)
                </label>
                <input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={mlQuantity}
                  onChange={(e) => setMlQuantity(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Ej: 50"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleMlConfirm();
                    if (e.key === 'Escape') handleMlCancel();
                  }}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Precio calculado
                </label>
                <div className="px-3 py-2 bg-white border rounded-lg font-bold text-green-600">
                  {mlQuantity && !isNaN(parseFloat(mlQuantity))
                    ? formatPrice(parseFloat(mlQuantity) * (mlProduct.price_per_ml ?? 0))
                    : '$ 0'}
                </div>
              </div>
              <div className="flex gap-2 pt-5">
                <Button size="sm" onClick={handleMlConfirm} disabled={!mlQuantity || parseFloat(mlQuantity) <= 0}>
                  Agregar
                </Button>
                <Button size="sm" variant="secondary" onClick={handleMlCancel}>
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Buscador y filtros */}
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar producto o servicio..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
          </div>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setTypeFilter('all')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                typeFilter === 'all' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Todos
            </button>
            <button
              type="button"
              onClick={() => setTypeFilter('product')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                typeFilter === 'product' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Package className="h-4 w-4" />
              Productos
            </button>
            <button
              type="button"
              onClick={() => setTypeFilter('service')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                typeFilter === 'service' ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Wrench className="h-4 w-4" />
              Servicios
            </button>
          </div>
        </div>

        {/* Lista de productos como tabla */}
        <div className="border rounded-lg max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : products.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {search ? 'No se encontraron productos' : 'Escriba para buscar productos'}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left p-3 font-medium text-gray-600">Producto</th>
                  <th className="text-center p-3 font-medium text-gray-600 w-20">Stock</th>
                  <th className="text-right p-3 font-medium text-gray-600 w-28">Precio</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {products.map((product) => {
                  const isProduct = !product.is_service;
                  const stock = product.current_stock ?? 0;
                  const noStock = isProduct && stock <= 0;

                  return (
                    <tr
                      key={product.id}
                      onClick={() => handleSelect(product)}
                      className={`cursor-pointer transition-colors ${
                        noStock
                          ? 'bg-red-50 hover:bg-red-100'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${product.is_service ? 'bg-purple-100' : 'bg-blue-100'}`}>
                            {product.is_service ? (
                              <Wrench className="h-4 w-4 text-purple-600" />
                            ) : (
                              <Package className="h-4 w-4 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <p className={`font-medium ${noStock ? 'text-gray-400' : 'text-gray-900'}`}>
                              {product.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {typeof product.category === 'object' && product.category?.name}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        {isProduct ? (
                          <span className={`font-medium ${
                            noStock ? 'text-red-500' : stock <= 5 ? 'text-orange-500' : 'text-gray-700'
                          }`}>
                            {stock}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        <span className={`font-bold ${noStock ? 'text-gray-400' : 'text-green-600'}`}>
                          {product.sale_price != null ? formatPrice(product.sale_price) : 'Variable'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Componente cobrar comanda
function ChargeOrderModal({
  order,
  isOpen,
  onClose,
  onCharge,
}: {
  order: ServiceOrder | null;
  isOpen: boolean;
  onClose: () => void;
  onCharge: (data: { orderId: number; paymentMethod?: PaymentMethod; paymentReference?: string }) => Promise<ServiceOrder>;
}) {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: categories } = useCategories();
  const { data: products } = useProducts({}, { enabled: isOpen });

  const PAYMENT_OPTIONS = [
    { value: 'cash', label: 'Efectivo' },
    { value: 'card', label: 'Tarjeta' },
    { value: 'transfer', label: 'Transferencia' },
    { value: 'other', label: 'Otro' },
  ];

  // Calcular total ajustado para tarjeta/transferencia
  const isCardOrTransfer = paymentMethod === 'card';
  const adjustedTotal = (() => {
    if (!order || !isCardOrTransfer) return order?.total || 0;

    const serviciosCat = categories?.find(
      (c) => c.name.toLowerCase() === 'servicios' && c.is_active
    );

    let total = 0;
    for (const item of order.items) {
      let adjustedPrice = item.unit_price;

      if (item.product_id) {
        // Buscar producto para usar list_price o multiplicador de categoría
        const product = products?.find((p) => p.id === item.product_id);
        if (product?.list_price) {
          adjustedPrice = product.list_price;
        } else if (product?.category_id) {
          const cat = categories?.find((c) => c.id === product.category_id);
          if (cat?.list_price_multiplier) {
            adjustedPrice = item.unit_price * cat.list_price_multiplier;
          }
        }
      } else if (serviciosCat?.list_price_multiplier) {
        // Item custom (servicio): aplicar multiplicador de categoría "Servicios"
        adjustedPrice = item.unit_price * serviciosCat.list_price_multiplier;
      }

      total += adjustedPrice * item.quantity;
    }
    return Math.round(total);
  })();

  const handleCharge = async () => {
    if (!order) return;
    setIsSubmitting(true);
    try {
      await onCharge({
        orderId: order.id,
        paymentMethod,
        paymentReference: paymentReference || undefined,
      });
      onClose();
      setPaymentMethod('cash');
      setPaymentReference('');
    } catch (error) {
      console.error('Error charging:', error);
    }
    setIsSubmitting(false);
  };

  if (!order) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cobrar Comanda" size="md">
      <div className="space-y-4">
        <div className="bg-gray-50 p-4 rounded-lg text-center">
          <p className="text-gray-500">Total a cobrar</p>
          <p className="text-3xl font-bold text-green-600">{formatPrice(adjustedTotal)}</p>
          {isCardOrTransfer && adjustedTotal !== order.total && (
            <p className="text-sm text-gray-500 mt-1">
              Efectivo: {formatPrice(order.total)} → +{Math.round(((adjustedTotal / order.total) - 1) * 100)}% recargo
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Método de Pago</label>
          <Select
            options={PAYMENT_OPTIONS}
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
          />
        </div>

        {paymentMethod !== 'cash' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Referencia</label>
            <Input
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="Número de comprobante, últimos 4 dígitos, etc."
            />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleCharge} loading={isSubmitting}>
            <DollarSign className="h-4 w-4 mr-1" />
            Confirmar Cobro
          </Button>
        </div>
      </div>
    </Modal>
  );
}
